import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { usePublicProductSearch, type PublicProductCandidate } from "@/hooks/use-returns";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Statusi koje vrijedi istaći; "instock" je normalno stanje i ne troši prostor u listi. */
const STOCK_NOTE: Record<string, string> = {
  outofstock: "nema na stanju",
  onbackorder: "na upit",
};

/** Rasprodat proizvod se i dalje može odabrati — kupac vraća ono što je kupio, ne ono što je
 *  danas na stanju — pa je oznaka informacija, a ne prepreka. */
function StockNote({ status }: { status?: string }) {
  const label = STOCK_NOTE[status || ""];
  if (!label) return null;
  return (
    <span className="shrink-0 text-[9px] uppercase tracking-[0.14em] text-foreground/70 ring-1 ring-foreground/15 px-1 py-px">
      {label}
    </span>
  );
}

type PublicProductPickerProps = {
  slug: string;
  value: PublicProductCandidate | null;
  onSelect: (candidate: PublicProductCandidate) => void;
  onClear: () => void;
  accent: string;
  label?: string;
  /** Poruka greške ispod polja (npr. proizvod nije odabran). */
  error?: string | null;
  /** Kad kupac bira šta će dobiti (zamjena, dodatna narudžba) nudi se samo roba na stanju. */
  inStockOnly?: boolean;
};

/** Pretraga kataloga na javnoj formi za povrat. Odabir je jedini način da se stavka unese —
 *  naziv i varijacija odabranog proizvoda su podaci koji idu u zahtjev. */
export function PublicProductPicker({
  slug,
  value,
  onSelect,
  onClear,
  accent,
  label = "Pronađite proizvod",
  error,
  inStockOnly = false,
}: PublicProductPickerProps) {
  const [queryInput, setQueryInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = usePublicProductSearch(slug, debouncedQuery, { inStockOnly });

  useEffect(() => {
    if (queryInput === debouncedQuery) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(queryInput), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [queryInput, debouncedQuery]);

  function handleSelect(candidate: PublicProductCandidate) {
    onSelect(candidate);
    setQueryInput("");
    setDebouncedQuery("");
    setFocused(false);
  }

  if (value) {
    return (
      <div className="space-y-1.5">
        <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</Label>
        <div className="flex items-center gap-2.5 ring-1 p-2" style={{ boxShadow: `inset 0 0 0 1px ${accent}` }}>
          {value.image ? (
            <img
              src={value.image}
              alt=""
              loading="lazy"
              className="size-10 object-cover shrink-0 ring-1 ring-foreground/10"
            />
          ) : (
            <div className="size-10 shrink-0 bg-muted ring-1 ring-foreground/10" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="truncate text-xs font-medium">{value.productName}</span>
              <StockNote status={value.stockStatus} />
            </div>
            {value.variationName && (
              <div className="truncate text-[10px] text-muted-foreground">{value.variationName}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClear}
            title="Ukloni odabrani proizvod"
            className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-destructive transition-colors px-1"
          >
            Ukloni
          </button>
        </div>
      </div>
    );
  }

  const term = debouncedQuery.trim();
  const showResults = focused && term.length >= 2;

  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground gap-1">
        {label}
        <span className="text-destructive">*</span>
      </Label>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
        <Input
          value={queryInput}
          onChange={(e) => setQueryInput(e.target.value)}
          onFocus={() => setFocused(true)}
          // Klik na rezultat prvo skida fokus s polja — kratka odgoda da se odabir stigne izvršiti.
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              setFocused(false);
            }
          }}
          placeholder={inStockOnly ? "Naziv proizvoda na stanju" : "Naziv proizvoda iz webshopa"}
          className="pl-8 pr-8"
        />
        {queryInput && (
          <button
            type="button"
            onClick={() => {
              setQueryInput("");
              setDebouncedQuery("");
            }}
            title="Obriši pretragu"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        )}

        {showResults && (
          <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-background ring-1 ring-foreground/10 shadow-lg max-h-64 overflow-y-auto">
            {search.isLoading && (
              <div className="px-3 py-2.5 text-[11px] text-muted-foreground">Tražim…</div>
            )}
            {!search.isLoading && search.isError && (
              <div className="px-3 py-2.5 text-[11px] text-destructive">
                {(search.error as any)?.message || "Pretraga trenutno nije dostupna"}
              </div>
            )}
            {!search.isLoading && !search.isError && (search.data?.candidates?.length ?? 0) === 0 && (
              <div className="px-3 py-2.5 text-[11px] text-muted-foreground">
                {inStockOnly
                  ? "Nema proizvoda na stanju za tu pretragu."
                  : "Nema rezultata — provjerite naziv proizvoda."}
              </div>
            )}
            {!search.isLoading &&
              !search.isError &&
              search.data?.candidates.map((c) => (
                <button
                  key={`${c.productId}-${c.variationId ?? "base"}`}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(c)}
                  className="w-full text-left px-2 py-1.5 hover:bg-muted/60 flex items-center gap-2.5 transition-colors"
                >
                  {c.image ? (
                    <img
                      src={c.image}
                      alt=""
                      loading="lazy"
                      className="size-9 object-cover shrink-0 ring-1 ring-foreground/10"
                    />
                  ) : (
                    <div className="size-9 shrink-0 bg-muted ring-1 ring-foreground/10" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="truncate text-xs">{c.productName}</span>
                      <StockNote status={c.stockStatus} />
                    </div>
                    {c.variationName && (
                      <div className="truncate text-[10px] text-muted-foreground">{c.variationName}</div>
                    )}
                  </div>
                </button>
              ))}
          </div>
        )}
      </div>
      {error ? (
        <p className="text-[10px] text-destructive">{error}</p>
      ) : (
        <p className="text-[10px] text-muted-foreground">
          Upišite dio naziva pa odaberite proizvod iz ponuđenih rezultata.
          {inStockOnly && " Nude se samo proizvodi koji su trenutno na stanju."}
        </p>
      )}
    </div>
  );
}
